// Web port of the iOS post-activation listening-level progression.
// Source of truth: client-native/src/features/activation/postActivationProgress.ts
// (DO NOT edit the iOS file). Ported verbatim; the only substitution is the
// level-curve import — iOS pulls `LEVELS` from `@/utils/progress/levels`, the
// PWA's faithful equivalent lives in `@/data/parrotLevels` (same `{ id, hours }`
// shape). Powers the module-complete thermometer once the 9-step activation
// journey is finished and progression switches to absolute listening goals.

import { LEVELS } from "@/data/parrotLevels";
import { ACTIVATION_LEVEL_COUNT } from "./activationSteps";

export type PendingPostActivationLevelUpRange = {
  fromLevel: number;
  toLevel: number;
};

export type PostActivationLevelGoal = {
  level: number;
  goalSeconds: number;
  label: string;
};

/**
 * The activation journey grants levels 1-10. After that, progression uses the
 * canonical absolute listening thresholds for levels 11-50.
 */
export const POST_ACTIVATION_LEVEL_THRESHOLDS: readonly {
  level: number;
  seconds: number;
}[] = LEVELS.filter((level) => level.id > ACTIVATION_LEVEL_COUNT + 1).map(
  (level) => ({
    level: level.id,
    seconds: level.hours * 3600,
  }),
);

export function getPostActivationLevelForListeningTime(
  totalSeconds: number,
): number {
  let level = ACTIVATION_LEVEL_COUNT + 1;
  for (const threshold of POST_ACTIVATION_LEVEL_THRESHOLDS) {
    if (totalSeconds >= threshold.seconds) {
      level = threshold.level;
    } else {
      break;
    }
  }
  return level;
}

function formatListeningGoal(seconds: number): string {
  const hours = seconds / 3600;
  if (Number.isInteger(hours)) return `${hours}h listening`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} min listening`;
}

export function getPostActivationLevelGoal({
  totalListeningTimeSeconds,
  pendingLevelUp,
}: {
  totalListeningTimeSeconds: number;
  pendingLevelUp: PendingPostActivationLevelUpRange | null;
}): PostActivationLevelGoal | null {
  const pendingTargetLevel =
    pendingLevelUp && pendingLevelUp.fromLevel < pendingLevelUp.toLevel
      ? pendingLevelUp.fromLevel + 1
      : null;
  const fallbackTargetLevel =
    getPostActivationLevelForListeningTime(totalListeningTimeSeconds) + 1;
  const targetLevel =
    pendingTargetLevel != null &&
    POST_ACTIVATION_LEVEL_THRESHOLDS.some(
      (entry) => entry.level === pendingTargetLevel,
    )
      ? pendingTargetLevel
      : fallbackTargetLevel;
  const threshold = POST_ACTIVATION_LEVEL_THRESHOLDS.find(
    (entry) => entry.level === targetLevel,
  );

  if (!threshold) return null;

  return {
    level: threshold.level,
    goalSeconds: threshold.seconds,
    label: formatListeningGoal(threshold.seconds),
  };
}
